/**
 * OrphanWellBanner.tsx
 * 
 * Task #92: Dashboard warning banner for orphan wells
 * 
 * Displays a prominent alert when wells exist without pumper assignment.
 * Severity-colored, dismissible, with direct action link.
 */

import { useState } from 'react';
import { AlertTriangle, X, RefreshCw, Users } from 'lucide-react';
import { cn } from '../ui/utils';
import { useOrphanWellsBanner } from '../../hooks/useOrphanWells';

interface OrphanWellBannerProps {
  /** Optional callback when action button clicked */
  onActionClick?: () => void;
  /** Allow dismissing the banner (reappears on refresh) */
  dismissible?: boolean;
  /** Compact mode for sidebar/header placement */
  compact?: boolean;
  /** Additional className */
  className?: string;
}

/** Tailwind color classes by severity */
const SEVERITY_STYLES = {
  none: {
    bg: 'bg-green-500/10',
    border: 'border-green-500/30',
    text: 'text-green-400',
    icon: 'text-green-500',
  },
  new: {
    bg: 'bg-yellow-500/10',
    border: 'border-yellow-500/30',
    text: 'text-yellow-400',
    icon: 'text-yellow-500',
  },
  warning: {
    bg: 'bg-orange-500/10',
    border: 'border-orange-500/30',
    text: 'text-orange-400',
    icon: 'text-orange-500',
  },
  critical: {
    bg: 'bg-red-500/10',
    border: 'border-red-500/30',
    text: 'text-red-400',
    icon: 'text-red-500',
  },
  emergency: {
    bg: 'bg-red-600/20',
    border: 'border-red-600/50',
    text: 'text-red-300',
    icon: 'text-red-500 animate-pulse',
  },
};

export default function OrphanWellBanner({
  onActionClick,
  dismissible = true,
  compact = false,
  className,
}: OrphanWellBannerProps) {
  const banner = useOrphanWellsBanner();
  const [dismissed, setDismissed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Don't show if no orphans or dismissed
  if (!banner.show || dismissed) {
    return null;
  }

  const styles = SEVERITY_STYLES[banner.severity] || SEVERITY_STYLES.warning;

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await banner.refresh();
    } finally {
      setRefreshing(false);
    }
  };

  const handleAction = () => {
    if (onActionClick) {
      onActionClick();
    } else {
      // Default: navigate to assignment page
      window.location.href = banner.actionUrl;
    }
  };

  // Compact mode for header/sidebar
  if (compact) {
    return (
      <button
        onClick={handleAction}
        className={cn(
          'flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors',
          styles.bg,
          styles.border,
          'hover:opacity-80',
          className
        )}
      >
        <span className="text-lg">{banner.icon}</span>
        <span className={cn('text-sm font-medium', styles.text)}>
          {banner.count} orphan{banner.count === 1 ? '' : 's'}
        </span>
      </button>
    );
  }

  // Full banner
  return (
    <div
      className={cn(
        'relative flex items-start gap-4 p-4 rounded-lg border',
        styles.bg,
        styles.border,
        className
      )}
    >
      {/* Icon */}
      <div className={cn('flex-shrink-0 mt-0.5', styles.icon)}>
        {banner.severity === 'emergency' ? (
          <AlertTriangle className="w-6 h-6" />
        ) : (
          <Users className="w-6 h-6" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Short message / title */}
        <p className={cn('font-semibold', styles.text)}>
          {banner.shortMessage}
        </p>
        
        {/* Full message */}
        <p className="mt-1 text-sm text-zinc-400 line-clamp-2">
          {banner.message}
        </p>

        {/* Actions */}
        <div className="mt-3 flex items-center gap-3">
          <button
            onClick={handleAction}
            className={cn(
              'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium',
              'bg-white/10 hover:bg-white/20 transition-colors',
              styles.text
            )}
          >
            {banner.actionLabel}
          </button>

          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-zinc-400 hover:text-zinc-300 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
            Refresh
          </button>
        </div>
      </div>

      {/* Dismiss button */}
      {dismissible && (
        <button
          onClick={() => setDismissed(true)}
          className="flex-shrink-0 p-1 text-zinc-500 hover:text-zinc-300 transition-colors"
          aria-label="Dismiss banner"
        >
          <X className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}

/**
 * Minimal badge version for nav/header
 */
export function OrphanWellBadge({ className }: { className?: string }) {
  const banner = useOrphanWellsBanner();

  if (!banner.show || banner.count === 0) {
    return null;
  }

  const styles = SEVERITY_STYLES[banner.severity] || SEVERITY_STYLES.warning;

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold',
        styles.bg,
        styles.text,
        banner.severity === 'emergency' && 'animate-pulse',
        className
      )}
    >
      {banner.count}
    </span>
  );
}
